// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { Camera, CameraDirection, CameraResultType, CameraSource } from "@capacitor/camera";
import { CONTRACTOR_BADGE_VERIFICATION_STATUS } from "@clipboard-health/contract-backend-main";
import { type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { useTheme } from "@mui/material";
import { FullScreenDialog, isCapacitorPlatform, useToast } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import {
  base64ToBlob,
  getFileTypeFromBlob,
  isAcceptableImageUploadContentType,
} from "@src/appV2/lib/files";
import { IGNORE_CAMERA_ERROR_MESSAGES } from "@src/appV2/lib/files/constants";
import { CameraPermissionDeniedError } from "@src/appV2/lib/Media/CameraPermissionDeniedError";
import { checkAndRequestPhotoPermissions } from "@src/appV2/lib/Media/checkAndRequestPhotoPermissions";
import { DEFAULT_CAMERA_IMAGE_QUALITY } from "@src/appV2/lib/Media/constants";
import { type UploadFileData } from "@src/appV2/lib/Media/types";
import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";

import { WorkBadgeReviewPolicySource } from "../../../../WorkBadges/components/Policy/types";
import { getWorkBadgeReviewPolicyUrl } from "../../../../WorkBadges/utils/getWorkBadgeReviewPolicyUrl";
import { useCreateContractorBadgeVerification } from "./api/useCreateContractorBadgeVerification";
import { useUpdateContractorBadgeVerification } from "./api/useUpdateContractorBadgeVerification";
import { useUploadBadgeVerificationPhoto } from "./api/useUploadBadgeVerificationPhoto";
import { BadgeVerificationConfirmContent } from "./BadgeVerificationConfirmContent";
import { BadgeVerificationIntroContent } from "./BadgeVerificationIntroContent";
import { BADGE_VERIFICATION_FLOW, BADGE_VERIFICATION_IMAGE_WIDTH } from "./constants";
import { NoBadgeContent } from "./NoBadgeContent";

type BadgeVerificationStep = "intro" | "confirm" | "no-badge";

interface BadgeVerificationPhoto extends UploadFileData {
  previewUrl: string;
}

interface BadgeVerificationDialogProps {
  shiftId: string;
  qualificationName: string;
  modalState: UseModalState;
  onSubmitPhoto: () => void;
  onSkipBadgeVerification: () => void;
}

export function BadgeVerificationDialog(props: BadgeVerificationDialogProps) {
  const { shiftId, qualificationName, modalState, onSubmitPhoto, onSkipBadgeVerification } = props;

  const history = useHistory();
  const theme = useTheme();
  const { showErrorToast } = useToast();

  const transitionDuration = theme.transitions.duration.leavingScreen;

  const [step, setStep] = useState<BadgeVerificationStep>("intro");
  const [photo, setPhoto] = useState<BadgeVerificationPhoto | undefined>(undefined);

  const { mutateAsync: createVerification, isLoading: isCreatingVerification } =
    useCreateContractorBadgeVerification();
  const { mutateAsync: updateVerification, isLoading: isUpdatingVerification } =
    useUpdateContractorBadgeVerification();
  const { mutateAsync: uploadToS3, isLoading: isUploadingToS3 } = useUploadBadgeVerificationPhoto();

  // Derive submitting state from mutations instead of manual state
  const isSubmitting = isCreatingVerification || isUploadingToS3 || isUpdatingVerification;

  useLogEffect(
    APP_V2_USER_EVENTS.BADGE_VERIFICATION_DIALOG_VIEWED,
    { shiftId },
    { enabled: modalState.modalIsOpen }
  );

  const resetState = useCallback(() => {
    setStep("intro");
    setPhoto(undefined);
  }, []);

  const handlePhotoSuccess = useCallback((photoData: UploadFileData) => {
    setPhoto({ ...photoData, previewUrl: photoData.dataUrlBase64 });
    setStep("confirm");
  }, []);

  const handleTakePhoto = useCallback(
    async (options?: { isRetake?: boolean }) => {
      const { isRetake = false } = options ?? {};

      logEvent(
        isRetake
          ? APP_V2_USER_EVENTS.BADGE_VERIFICATION_RETAKE_PHOTO_TAPPED
          : APP_V2_USER_EVENTS.BADGE_VERIFICATION_TAKE_PHOTO_TAPPED,
        { shiftId }
      );

      try {
        // On mobile, use the camera to take a photo. On web, allow picking files so the flow can be tested.
        const cameraSource = isCapacitorPlatform() ? CameraSource.Camera : CameraSource.Photos;

        await checkAndRequestPhotoPermissions(cameraSource);

        const { dataUrl } = await Camera.getPhoto({
          source: cameraSource,
          resultType: CameraResultType.DataUrl,
          // Use front camera for badge verification (selfie with badge)
          direction: CameraDirection.Front,
          quality: DEFAULT_CAMERA_IMAGE_QUALITY,
          // Higher width for better quality required for verification
          width: BADGE_VERIFICATION_IMAGE_WIDTH,
        });

        if (!isDefined(dataUrl)) {
          throw new Error("Invalid Camera Result");
        }

        const blob = await base64ToBlob(dataUrl);
        const { extension, contentType } = await getFileTypeFromBlob(blob);

        if (!isAcceptableImageUploadContentType(contentType)) {
          throw new Error(`Image with unsupported content type picked: ${contentType}`);
        }

        handlePhotoSuccess({
          dataUrlBase64: dataUrl,
          blob,
          extension,
          contentType,
        });
      } catch (error) {
        logError(APP_V2_APP_EVENTS.BADGE_VERIFICATION_TAKE_PHOTO_FAILURE, { error });

        if (error instanceof CameraPermissionDeniedError) {
          showErrorToast("Camera permission is required to take a badge photo.");
        } else if (error instanceof Error && IGNORE_CAMERA_ERROR_MESSAGES.has(error.message)) {
          // User cancelled camera - don't show error toast
        } else {
          showErrorToast("Failed to take photo. Please try again.");
        }
      }
    },
    [handlePhotoSuccess, shiftId, showErrorToast]
  );

  const handleSubmit = useCallback(async () => {
    if (!isDefined(photo)) {
      return;
    }

    try {
      // Step 1: Create verification record and get presigned S3 upload URL
      const { data: verification } = await createVerification({ shiftId });
      const { uploadUrl } = verification.attributes;
      const verificationId = verification.id;

      // Step 2: Upload photo to S3 using presigned URL
      await uploadToS3({ uploadUrl, photoBlob: photo.blob });

      // Step 3: Confirm the upload by updating verification status
      await updateVerification({
        verificationId,
        status: CONTRACTOR_BADGE_VERIFICATION_STATUS.UPLOADED,
        shiftId,
      });

      logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_PHOTO_SUBMITTED, { shiftId });
      onSubmitPhoto();
    } catch (error) {
      logError(APP_V2_APP_EVENTS.BADGE_VERIFICATION_SUBMIT_FAILURE, {
        error,
        metadata: { shiftId, flow: BADGE_VERIFICATION_FLOW.UPLOAD },
      });
      showErrorToast("Failed to submit badge photo. Please try again.");
    }
  }, [
    photo,
    shiftId,
    createVerification,
    uploadToS3,
    updateVerification,
    onSubmitPhoto,
    showErrorToast,
  ]);

  const handleSkipToClockIn = useCallback(async () => {
    try {
      // Step 1: Create verification record (idempotent - will return existing if exists)
      const createResponse = await createVerification({ shiftId });
      const verificationId = createResponse.data.id;

      // Step 2: Mark verification as skipped
      await updateVerification({
        verificationId,
        status: CONTRACTOR_BADGE_VERIFICATION_STATUS.SKIPPED,
        shiftId,
      });

      logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_SKIPPED, { shiftId });
      onSkipBadgeVerification();
    } catch (error) {
      logError(APP_V2_APP_EVENTS.BADGE_VERIFICATION_SUBMIT_FAILURE, {
        error,
        metadata: { shiftId, flow: BADGE_VERIFICATION_FLOW.SKIP },
      });
      showErrorToast("Failed to skip badge verification. Please try again.");
    }
  }, [shiftId, createVerification, updateVerification, onSkipBadgeVerification, showErrorToast]);

  const handleClose = useCallback(() => {
    modalState.closeModal();
    // Reset state after modal exit transition completes
    setTimeout(resetState, transitionDuration);
  }, [modalState, resetState, transitionDuration]);

  return (
    <FullScreenDialog
      skipTopSafeAreaPadding
      modalState={modalState}
      variant="tertiary"
      // Disable transition so navigating back from policy page opens dialog instantly
      transitionDuration={0}
    >
      {step === "intro" && (
        <BadgeVerificationIntroContent
          onBack={handleClose}
          onTakePhoto={() => {
            void handleTakePhoto();
          }}
          onNoBadge={() => {
            logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_NO_BADGE_TAPPED, { shiftId });
            setStep("no-badge");
          }}
          onWhyBadgeRequired={() => {
            logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_WHY_BADGE_REQUIRED_TAPPED, { shiftId });
            modalState.closeModal();
            history.replace(
              getWorkBadgeReviewPolicyUrl({
                source: WorkBadgeReviewPolicySource.BADGE_VERIFICATION,
                shiftId,
                qualificationName,
              })
            );
          }}
        />
      )}

      {step === "confirm" && isDefined(photo) && (
        <BadgeVerificationConfirmContent
          photoPreviewUrl={photo.previewUrl}
          isSubmitting={isSubmitting}
          onBack={() => {
            setStep("intro");
            setPhoto(undefined);
          }}
          onSubmit={handleSubmit}
          onRetakePhoto={() => {
            void handleTakePhoto({ isRetake: true });
          }}
        />
      )}

      {step === "no-badge" && (
        <NoBadgeContent
          isSubmitting={isSubmitting}
          onBack={() => {
            logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_HAS_BADGE_TAPPED, { shiftId });
            setStep("intro");
          }}
          onClose={handleClose}
          onSkipToClockIn={handleSkipToClockIn}
        />
      )}
    </FullScreenDialog>
  );
}
